import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, input, output, signal } from "@angular/core";
import { MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import type { InterruptItem } from "../../../model/interrupt.types";
import { HitlAskContextComponent } from "../hitl-ask-context/hitl-ask-context.component";
import { NotificationCardComponent } from "../../shared/notification-card/notification-card.component";

type CardState = "pending" | "resolved-confirm" | "resolved-decline" | "expired";

const TIMEOUT_MS = 5 * 60 * 1000;

/**
 * HITL card for kind === 'boolean'.
 * Emits "yes" or "no" as a plain string matching the backend contract.
 */
@Component({
  selector: "app-hitl-confirm-card",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatIcon, HitlAskContextComponent, NotificationCardComponent],
  templateUrl: "./hitl-confirm-card.component.html",
  styleUrl: "./hitl-confirm-card.component.scss",
})
export class HitlConfirmCardComponent implements OnInit {
  entry = input.required<InterruptItem>();

  readonly submitted = output<string>();

  protected readonly state = signal<CardState>("pending");

  readonly #destroyRef = inject(DestroyRef);

  private timeoutHandle?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    this.timeoutHandle = setTimeout(() => this.expire(), TIMEOUT_MS);
    this.#destroyRef.onDestroy(() => clearTimeout(this.timeoutHandle));
  }

  protected confirm(): void {
    clearTimeout(this.timeoutHandle);
    this.state.set("resolved-confirm");
    this.submitted.emit("true");
  }

  protected decline(): void {
    clearTimeout(this.timeoutHandle);
    this.state.set("resolved-decline");
    this.submitted.emit("false");
  }

  protected restart(): void {
    clearTimeout(this.timeoutHandle);
    this.state.set("pending");
    this.timeoutHandle = setTimeout(() => this.expire(), TIMEOUT_MS);
  }

  private expire(): void {
    this.state.set("expired");
  }
}
